import { t } from "../utils/i18n.ts";
import { UI_SPACE, withUiScale } from "./uiTokens.ts";
import {
  MODAL_SHELL,
  ModalShell,
  modalButton,
  modalHeader,
  modalIconButton,
  modalRule,
} from "./modalShell.ts";

/** The pause glyph on the header badge — the mockup's own mark
 *  (`docs/pause_settings_overlays_2026-08.html`, the `.badge` in §6). Not an
 *  i18n string: it is a symbol, identical in every locale. */
const PAUSE_GLYPH = "❚❚";
/** The settings affordance glyph — the same ⚙ the HUD gear draws. */
const GEAR_GLYPH = "⚙";

const STYLE_ID = "pause-overlay-styles";

export interface PauseOverlayActions {
  /** RUN NEU STARTEN — emits the SAME `game.restart` the gear row emits today. */
  onRestart?: () => void;
  /** ZURÜCK ZUM MENÜ — emits the SAME `game.returnToMenu` as today. */
  onExit?: () => void;
  /**
   * Swap to the settings modal (spec AC-16). The ⚙ affordance is rendered ONLY
   * when this is supplied, so the pause modal never ships a dead button.
   * `main.ts` wires it to `HUD.openSettings`, which takes THIS modal down before
   * showing settings (never two surfaces, AC-7) and sends `ZURÜCK`/Escape back
   * here because this modal was up when settings opened.
   */
  onOpenSettings?: () => void;
}

/**
 * The in-run PAUSE modal — a CONTENT FILL on the shared modal shell
 * (`modalShell.ts`, spec `2026-pause-settings-overlays` AC-2/AC-3).
 *
 * It declares NO backdrop and NO panel material of its own: the wash, the lit
 * panel, the corner brackets, the head roles, the rule, the button ranks and
 * the enter/leave transition all come from the shell, so the settings modal is
 * the same hardware rather than a lookalike. What lives here is only the fill:
 * the panel's width/padding, the centered head, the stacked action column and
 * the ⚙ placement.
 *
 * Contents, per the owner-approved mockup:
 *   ❚❚ badge · PAUSIERT · "Klicken oder ESC zum Fortsetzen"
 *   ─────
 *   ▶ WEITER (primary) · ↻ RUN NEU STARTEN (secondary) · ⏻ ZURÜCK ZUM MENÜ (exit)
 *
 * **No keycap chips** (owner decision OQ-3 = b, 2026-08-09): the dropdown's `R`
 * chip promised a shortcut that does not exist and its `Esc` chip claimed the
 * opposite of what Escape does (`inputHandlers.ts:408-411` — Escape RESUMES).
 * No `R` shortcut is implemented. The subtitle still names ESC because that
 * sentence is prose and it is true.
 *
 * Dismissal is unchanged (BL-198 / `UI_DISMISS`): a click on the dimmed backdrop
 * resumes, the WEITER button resumes, a click on the card does not — and Escape
 * keeps being owned by `inputHandlers.ts`, not by this overlay.
 *
 * ── Panel width: 380 design px — the mockup's own number ─────────────────────
 * `docs/pause_settings_overlays_2026-08.html:505` draws
 * `.m-pause { width: calc(380 * var(--k)) }` and `--k` is one design px, so the
 * number transfers 1:1. It is KEPT even though the functional strings had to
 * move up to `modalShell.MODAL_TEXT_FLOOR_ROLE` (20 design px) for the ≥11 CSS
 * px FINAL floor, because the bigger type still fits: VT323's advance is
 * **0.4000 em** for every drawn glyph (`hmtx`/`head` of the shipped
 * `public/fonts/VT323-Regular.ttf`: `advanceWidth` 400, `unitsPerEm` 1000), and
 * the longest line on the screen — the DE subtitle — lands at **82.2 %** of the
 * panel's content box on desktop and iPad, 76.9-77.3 % on the two phones
 * (measured in a real browser on the shipped page, 2026-08-10, DE + EN × four
 * viewports: every one of the five strings renders on ONE line, tightest CTA
 * headroom 92.5 px real). Widening buys margin the floor does not need.
 *
 * **Re-measured 2026-08-10 after Task-003** moved the panel padding into the
 * shell AND scaled it with the panel (it was flat design px, so the content box
 * used to GROW relative to its own text on a phone). Every design-px value here
 * is unchanged — 380 wide, 26/30/28 padding, 12 head gap, 4 subtitle offset,
 * 20/18 rule margin, 22 ⚙ inset — but the content box is now `(380 − 60) × k`
 * instead of `380 × k − 60`, so the same DE subtitle occupies **85.3 %**
 * (desktop/iPad), 83.2 % (14 Pro), 83.1 % (SE): one line everywhere, ~15 %
 * headroom, and now the SAME proportion at every tier instead of drifting 77 →
 * 82 %. That uniformity is the point; the ~3-6 pp of headroom it costs is the
 * price, and it is paid where there was the most slack. (Arithmetic from the
 * VT323 advance above; the model reproduces the four real-browser measurements
 * quoted in the previous paragraph to 0.1 pp, which is why it is trusted here.)
 *
 * It also costs shape. The drawn panel is 379.2 × 367.5 design px → w:h **1.03**;
 * measured in game at 380 the ratio is 1.10 (desktop/iPad), 1.26 / 1.28 (iPhone
 * 14 Pro / SE), and at 440 it goes to 1.28 / 1.46 / 1.48 — i.e. the extra width
 * walks away from the drawing on every tier. Share of the frame at 380: 29.7 %
 * (desktop), 36.9 % (iPad), 32.9 % (14 Pro), 41.3 % (SE); height 47.9 % / 47.9 %
 * / 56.5 % / 57.4 %, so it fits vertically everywhere. The mockup's own 39.5 %
 * is a share of its 960×672 stage, which its CSS comment calls out as *"das
 * echte Brett (20×14 Zellen à 48px)"* — the BOARD, not the screen — so it is not
 * a share-of-frame target to match.
 */
export class PauseOverlay {
  private shell: ModalShell;
  private onResume: (() => void) | null;
  private onRestart: (() => void) | null;
  private onExit: (() => void) | null;

  constructor(
    parent: HTMLElement,
    onResume?: () => void,
    actions: PauseOverlayActions = {},
  ) {
    this.onResume = onResume ?? null;
    this.onRestart = actions.onRestart ?? null;
    this.onExit = actions.onExit ?? null;
    PauseOverlay.injectStyles();

    this.shell = new ModalShell(parent, {
      id: "pause-overlay",
      panelId: "pause-card",
      panelClass: "pause-modal",
      labelledBy: "pause-title",
      // BL-198 dismiss contract — the dimmed area resumes (the subtitle promises
      // exactly this); the panel itself never does.
      onBackdropClick: () => this.onResume?.(),
      // AC-8 — Escape does exactly what the subtitle promises and exactly what
      // WEITER does: it resumes, once. The shell owns the press while the modal
      // is open (capture phase), so it can no longer ALSO reach a gameplay
      // branch in `inputHandlers.ts`. Same callback as the backdrop and the
      // primary CTA, so the three dismissals cannot drift apart.
      onEscape: () => this.onResume?.(),
    });

    // The ⚙ swap affordance — rendered ONLY when a settings target exists.
    if (actions.onOpenSettings) {
      const openSettings = actions.onOpenSettings;
      this.shell.append(
        modalIconButton({
          id: "pause-settings-btn",
          glyph: GEAR_GLYPH,
          label: t("hud.gear"),
          onActivate: () => openSettings(),
        }),
      );
    }

    this.shell.append(
      modalHeader({
        icon: PAUSE_GLYPH,
        title: t("pause.title"),
        subtitle: t("pause.subtitle"),
        // The same line, told truthfully to a finger. See modalShell's
        // pointer:coarse gate — the shell renders both and shows one.
        subtitleCoarse: t("pause.subtitleTouch"),
        titleId: "pause-title",
        subtitleId: "pause-subtitle",
      }),
      modalRule(),
    );

    // The three actions, stacked full-width in mockup order. Their labels are
    // the ALREADY-SHIPPED EN+DE keys the gear rows use, so the copy (and its
    // glyphs) moves surface without a re-translation. All three always render —
    // their handlers are injected exactly like `onResume` (a missing one is a
    // no-op, never a missing control).
    const actionsCol = document.createElement("div");
    actionsCol.id = "pause-actions";
    actionsCol.appendChild(
      modalButton({
        id: "pause-resume-btn",
        label: t("hud.resume"),
        rank: "primary",
        onActivate: () => this.onResume?.(),
      }),
    );
    actionsCol.appendChild(
      modalButton({
        id: "pause-restart-btn",
        label: t("hud.restartRun"),
        rank: "secondary",
        onActivate: () => this.onRestart?.(),
      }),
    );
    actionsCol.appendChild(
      modalButton({
        id: "pause-exit-btn",
        label: t("hud.exitToMenu"),
        rank: "exit",
        onActivate: () => this.onExit?.(),
      }),
    );
    this.shell.append(actionsCol);
  }

  show(): void {
    this.shell.show();
  }

  hide(): void {
    this.shell.hide();
  }

  get visible(): boolean {
    return this.shell.visible;
  }

  destroy(): void {
    this.shell.destroy();
  }

  /**
   * The pause modal's CONTENT-ONLY stylesheet: panel size, the centered head,
   * the stacked action column and the ⚙ placement. Deliberately carries no
   * backdrop, no panel material and no button ranks — those are the shell's
   * (AC-2), including the reduced-motion seam (AC-14).
   *
   * Task-003 moved five more declarations OUT of here and into the shell, where
   * both fills now inherit them at ONE scale: the panel padding, the head gap,
   * the subtitle offset and the rule margin (their values are unchanged — the
   * shell adopted the pause modal's own numbers, so this modal renders the same
   * geometry, now `--ui-scale`d). What stays is what genuinely differs between
   * the two fills: the width, the COLUMN head, the stacked action list and the
   * ⚙ placement.
   *
   * Task-005 (AC-11) moved ONE number: `#pause-actions`'s `gap`, `md` (10) →
   * `lg` (12). The mockup's drawn 10 (`docs/pause_settings_overlays_2026-08.html
   * :511`) measured **7.24 CSS px** between two stacked CTAs on an iPhone SE
   * and **7.38** on a 14 Pro — under the ≥8 CSS px touch-spacing rail; at `lg`
   * they are **8.69** / **8.86**. Real Chromium, `pointer:coarse`, both phone
   * tiers, re-measured 2026-08-11, and reproducible from the computed
   * `row-gap` alone: 17.3891 design px × k 0.724455 = 7.244 (the earlier 6.82
   * in this note does not follow from any shipped declaration at that k and did
   * not re-measure). `lg` is the smallest step on the shipped ladder that clears
   * the rail; the reasoning lives in `modalShell.modalShellStyles`.
   */
  private static injectStyles(): void {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #pause-card.pause-modal {
        width: ${withUiScale(380)};
        max-width: 86%;
      }
      #pause-card .${MODAL_SHELL.iconButton} {
        top: ${withUiScale(UI_SPACE.xxl - 2)};
        right: ${withUiScale(UI_SPACE.xxl - 2)};
      }
      #pause-card .${MODAL_SHELL.head} {
        flex-direction: column;
        text-align: center;
        padding-top: ${withUiScale(UI_SPACE.xs)};
      }
      #pause-actions {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: ${withUiScale(UI_SPACE.lg)};
      }
      #pause-actions .${MODAL_SHELL.cta} { width: 100%; }
    `;
    document.head.appendChild(style);
  }
}
